<style lang="scss">
  @use "../../../assets/css/design-system/colors" as colors;
  :global {
    body.no-scroll {
      overflow: hidden;
    }
  }

  .svelte-modal-overlay {
    width: 100%;
    height: 100%;
    align-items: center;
    background: #00000066;
    display: flex;
    left: 0;
    justify-content: center;
    position: fixed;
    top: 0;
    transition: backdrop-filter 0.3s ease-in-out;
    z-index: 1000;

    > .sliders-and-content {
      display: flex;
      align-items: center;

      > .svelte-modal-contents {
        animation: zoom 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        display: flex;
        flex-direction: column;
        background: white;
        border-radius: 20px;
        overflow: hidden;
        position: relative;

        @keyframes zoom {
          from {
            transform: scale(0.95);
          }

          to {
            transform: scale(1);
          }
        }

        &.small {
          width: calc(min(90vw, 25rem));
        }

        &.medium {
          width: calc(min(90vw, 37.5rem));
          height: calc(min(90vh, 40rem));
        }

        &.large {
          width: calc(min(90vw, 67.5rem));
          height: calc(min(90svh, 60rem));
        }

        &.xl {
          width: calc(min(92.5vw, 110rem));
          height: calc(min(95svh, 70rem));
        }

        > .svelte-modal-header {
          width: 100%;
          align-items: center;
          display: flex;
          flex: 0;
          flex-direction: row;
          padding: 1rem;
          justify-content: space-between;
          z-index: 9;
          &.empty {
            justify-content: flex-end;
          }

          &.border-bottom {
            border-bottom: 1px solid colors.get(interfaceSwGrey_10);
          }
        }

        > .svelte-modal-body {
          flex: 1;
          width: 100%;
          overflow: hidden;
          border-radius: 0 0 20px 20px;
        }
      }
    }
  }
</style>

<script>
  import { onDestroy, onMount } from "svelte";
  import {
    makeEscapeAction,
    makeOnMouseDownUpAction,
  } from "../../../utils/escapable";
  import CloseButton from "./CloseButton.svelte";
  import Slider from "./Slider.svelte";
  import { modalCountStore } from "./stores";

  export let onclose = () => {}; // Called when modal close button is clicked - use this to set wrapping component {#if} condition to falsy.
  export let el; // A reference to the modal element - useful for floating-ui boundaries
  export let left = null; // Optionally pass a function to be called when the left slider is clicked.
  export let right = null; // Optionally pass a function to be called when the right slider is clicked.
  export let size = "";
  export let blur = "0px";
  export let closeButton = true; // Can remove x button in top right corner e.g. for pop-up modals
  export let closeButtonStyle = ""; // Custom styles for the close button, e.g. to add padding
  export let showHeader = true; // Can remove header e.g. for a contact modal
  export let headerBorderBottom = false; // Can add a border line to separate header from body

  export let closeOnOutsideClick = true;

  // Styling props
  export let modalContentsStyle = ""; // Custom styles for the modal contents
  export let bodyStyle = ""; // Custom styles for the modal body
  export let headerStyle = ""; // Custom styles for the modal header

  const empty = !$$slots.header;

  const escape = makeEscapeAction(onclose);
  const fullClick = makeOnMouseDownUpAction(() => {
    if (closeOnOutsideClick) {
      onclose();
    }
  });
  let headerHeight = 0;

  let modalBodyStyle = "height: 100%";

  $: requestAnimationFrame(() => {
    modalBodyStyle = `height: calc(100% - ${headerHeight}px); ${bodyStyle}`;
  });

  $: blurStyle = blur === "0px" ? "" : `backdrop-filter: blur(${blur});`;

  onMount(() => {
    $modalCountStore += 1;
    if ($modalCountStore === 1) {
      document.body.classList.add("no-scroll");
    }
  });

  onDestroy(() => {
    $modalCountStore -= 1;
    if (!$modalCountStore) {
      document.body.classList.remove("no-scroll");
    }
  });
</script>

<div class="svelte-modal-overlay" style={blurStyle} use:escape use:fullClick>
  <div class="sliders-and-content">
    {#if left || right}
      <Slider side="left" onclick={left} />
    {/if}
    <div
      class="svelte-modal-contents {size}"
      style={modalContentsStyle}
      bind:this={el}
    >
      {#if showHeader}
        <div
          class="svelte-modal-header"
          class:empty
          class:border-bottom={headerBorderBottom}
          bind:clientHeight={headerHeight}
          style={headerStyle}
        >
          <slot name="header" />
          {#if closeButton}
            <div class="close-button" style={closeButtonStyle}>
              <CloseButton {onclose} />
            </div>
          {/if}
        </div>
      {/if}
      <div class="svelte-modal-body" style={modalBodyStyle}>
        <slot />
      </div>
    </div>
    {#if left || right}
      <Slider side="right" onclick={right} />
    {/if}
  </div>
</div>
